/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */


import * as $ from "jquery";
import {UiComponentConfig} from "../generated/UiComponentConfig";
import {UiToolbar} from "./UiToolbar";
import {UiToolbarConfig} from "../generated/UiToolbarConfig";
import {UiNavigationBar} from "./UiNavigationBar";
import {UiNavigationBarConfig} from "../generated/UiNavigationBarConfig";
import {UiComponent} from "./UiComponent";
import {TeamAppsUiContext} from "./TeamAppsUiContext";
import {UiMobileLayoutConfig} from "../generated/UiMobileLayoutConfig";
import {TeamAppsUiComponentRegistry} from "./TeamAppsUiComponentRegistry";
import {UiMobileLayoutAnimation} from "../generated/UiMobileLayoutAnimation";

/**
 * @author Yann Massard (yamass@gmail.com)
 */
class View {
	component: UiComponent<UiComponentConfig>;
	$container: JQuery;
}

export class UiMobileLayout extends UiComponent<UiMobileLayoutConfig> {

	private $mainDiv: JQuery;
	private $toolbarContainer: JQuery;
	private $contentContainerWrapper: JQuery;
	private $navBarContainer: JQuery;

	private toolbar: UiToolbar;
	private navBar: UiNavigationBar;
	private views: { [id: string]: View } = {};
	private currentView: View;

	constructor(config: UiMobileLayoutConfig, context: TeamAppsUiContext) {
		super(config, context);
		this.$mainDiv = $(`<div id="${config.id}" class="UiMobileLayout">
                             <div class="toolbar-container"></div>
                             <div class="content-container-wrapper"></div>
                             <div class="navigation-bar-container"></div>
            </div>`);

		this.$toolbarContainer = this.$mainDiv.find('>.toolbar-container');
		this.$contentContainerWrapper = this.$mainDiv.find('>.content-container-wrapper');
		this.$navBarContainer = this.$mainDiv.find('>.navigation-bar-container');

		this.setToolbar(config.toolbar);
		this.setNavigationBar(config.navigationBar);

		if (config.views) {
			config.views.forEach(v => this.addView(v));
		}

		if (config.initialViewId) {
			this.showView(config.initialViewId, null);
		}
	}

	public addView(viewConfig: UiComponentConfig) {
		var $container = $(`<div class="content-container">`);
		this.$contentContainerWrapper.append($container);
		var viewComponent = this._context.createComponent(viewConfig);
		viewComponent.getMainDomElement().appendTo($container);
		this.views[viewConfig.id] = {
			component: viewComponent,
			$container: $container
		};
		this.resizeChildren();
		viewComponent.attachedToDom = this.attachedToDom;
	}

	public removeView(viewId: string) {
		let view = this.views[viewId];
		view.$container.remove();
		delete this.views[viewId];
	}

	public showView(viewId: string, animationType: UiMobileLayoutAnimation) {
		var newView = this.views[viewId];

		if (newView == null) {
			this.logger.warn("View with id " + viewId + " not registered!");
			return;
		}
		if (newView === this.currentView) {
			return;
		}

		if (animationType == UiMobileLayoutAnimation.FORWARD) {
			this.doViewTransition(newView, "forward-offsite", "backward-offsite");
		} else if (animationType == UiMobileLayoutAnimation.BACKWARD) {
			this.doViewTransition(newView, "backward-offsite", "forward-offsite");
		} else {
			newView.$container.addClass("active");
			this.currentView && this.currentView.$container.removeClass("active");
		}

		newView.component.attachedToDom = true;
		newView.component.reLayout();

		this.currentView = newView;
	}

	private doViewTransition(newView: View, inwardStyle: string, outwardStyle: string) {
		newView.$container.addClass(inwardStyle + " active")[0].offsetWidth;
		this.$mainDiv.addClass("transitions")[0].offsetWidth;
		this.currentView && this.currentView.$container.addClass(outwardStyle);
		newView.$container.removeClass(inwardStyle);

		let currentViewLocalVar = this.currentView;
		setTimeout(() => {
			currentViewLocalVar && currentViewLocalVar.$container.removeClass("active " + outwardStyle);
			this.$mainDiv.removeClass("transitions");
		}, 500);
	};

	public onResize(): void {
		this.toolbar && this.toolbar.reLayout();
		this.resizeChildren();
		this.navBar && this.navBar.reLayout(true);
	}

	private resizeChildren() {
		let dimensionCss = this.$contentContainerWrapper.css(["width", "height"]);
		Object.keys(this.views).forEach(viewId => {
			let view = this.views[viewId];
			view.$container.css(dimensionCss);
			view.component.reLayout();
		});
	}

	public setToolbar(toolbar: UiToolbarConfig): void {
		if (this.toolbar) {
			this.$toolbarContainer.empty();
			this.toolbar.destroy();
			this.toolbar = null;
		}
		this.$toolbarContainer.toggleClass('hidden', !toolbar);
		if (toolbar) {
			this.toolbar = <UiToolbar> this._context.createComponent(toolbar);
			this.toolbar.getMainDomElement().appendTo(this.$toolbarContainer);
			this.toolbar.attachedToDom = this.attachedToDom;
		}
	}

	public setNavigationBar(navBarConfig: UiNavigationBarConfig) {
		if (this.navBar) {
			this.$navBarContainer.empty();
			this.navBar.destroy();
			this.navBar = null;
		}
		this.$navBarContainer.toggleClass('hidden', !navBarConfig);
		if (navBarConfig) {
			this.navBar = <UiNavigationBar> this._context.createComponent(navBarConfig);
			this.navBar.getMainDomElement().appendTo(this.$navBarContainer);
			this.navBar.attachedToDom = this.attachedToDom;
		}
	}

	public getMainDomElement(): JQuery {
		return this.$mainDiv;
	}

	protected onAttachedToDom() {
		if (this.toolbar) this.toolbar.attachedToDom = true;
		if (this.navBar) this.navBar.attachedToDom = true;
		if (this.currentView) this.currentView.component.attachedToDom = true;
	}

	public destroy(): void {
		if (this.toolbar) this.toolbar.destroy();
		Object.keys(this.views).forEach(viewId => this.views[viewId].component.destroy());
	}
}

TeamAppsUiComponentRegistry.registerComponentClass("UiMobileLayout", UiMobileLayout);